import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Download, Search, Plus, Edit2, Trash2, Phone, Mail,
  RefreshCw, User, Send, Loader2, MessageSquare,
  Users, TrendingUp, UserCheck, X, ChevronDown, ChevronUp,
  Bold, Italic, Strikethrough, Hash, Link2, ArrowRight,
  Image, Video, FileText, Type, Play, Check, CheckCheck,
  Smile, Sparkles, LayoutTemplate, AlertCircle, Globe,
  Eye, Copy, Star, Zap, Bot, Megaphone, Tag, Package,
  Filter, SlidersHorizontal, Clock, BarChart2, Workflow,
} from 'lucide-react';
import { Input }    from '@/components/ui/input';
import { Label }    from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch }   from '@/components/ui/switch';
import { Badge }    from '@/components/ui/badge';
import {
  Table, TableBody, TableCell,
  TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader,
  DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem,
  SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api       from '@/lib/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0  },
  transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1], delay },
});

const STATUS_MAP = {
  new:       { label: 'New',       dot: 'bg-blue-500',    pill: 'bg-blue-50   text-blue-700   border-blue-200'   },
  contacted: { label: 'Contacted', dot: 'bg-amber-500',   pill: 'bg-amber-50  text-amber-700  border-amber-200'  },
  qualified: { label: 'Qualified', dot: 'bg-emerald-500', pill: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  converted: { label: 'Converted', dot: 'bg-violet-500',  pill: 'bg-violet-50 text-violet-700 border-violet-200' },
  lost:      { label: 'Lost',      dot: 'bg-rose-400',    pill: 'bg-rose-50   text-rose-700   border-rose-200'   },
};

const SOURCE_ICON = {
  whatsapp: '💬', website: '🌐', referral: '🤝', manual: '✏️', other: '📌',
};

const TEMPLATE_CATEGORIES = [
  { value: 'sales',     label: 'Sales',          emoji: '💰', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'followup',  label: 'Follow-up',       emoji: '🔄', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  { value: 'promotion', label: 'Promotion',       emoji: '📣', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  { value: 'support',   label: 'Support',         emoji: '🎧', color: 'bg-pink-100 text-pink-700 border-pink-200' },
  { value: 'nurture',   label: 'Lead Nurture',    emoji: '🌱', color: 'bg-teal-100 text-teal-700 border-teal-200' },
  { value: 'reengagement', label: 'Re-engagement',emoji: '🔥', color: 'bg-orange-100 text-orange-700 border-orange-200' },
];

const HEADER_TYPES = [
  { value: 'none',     label: 'No Header',  icon: X },
  { value: 'text',     label: 'Text',       icon: Type },
  { value: 'image',    label: 'Image',      icon: Image },
  { value: 'video',    label: 'Video',      icon: Video },
  { value: 'document', label: 'Document',   icon: FileText },
];

const BUTTON_TYPES = [
  { value: 'none',        label: 'No Buttons'    },
  { value: 'cta',         label: 'Call to Action' },
  { value: 'quick_reply', label: 'Quick Replies'  },
];

// ─── Pre-built Lead Templates ─────────────────────────────────────────────────
const STARTER_TEMPLATES = [
  {
    id: 'l1',
    name: 'Warm Welcome',
    category: 'sales',
    headerType: 'text',
    headerText: '👋 Welcome!',
    body: `Hi *{{1}}*! 👋\n\nThank you for reaching out to *{{2}}*.\n\nWe're excited to connect with you! Here's how we can help:\n\n✅ Personalized solutions\n✅ Expert guidance\n✅ 24/7 support\n\nWould you like to schedule a quick call to discuss your requirements?\n\n_We typically respond within 1 business hour._`,
    footer: 'Reply STOP to unsubscribe',
    buttonType: 'quick_reply',
    buttons: [
      { type: 'quick_reply', text: 'Yes, Schedule Call', value: 'SCHEDULE' },
      { type: 'quick_reply', text: 'Tell me more', value: 'MORE_INFO' },
    ],
  },
  {
    id: 'l2',
    name: 'Follow-up After Inquiry',
    category: 'followup',
    headerType: 'text',
    headerText: '🔄 Quick Follow-up',
    body: `Hi *{{1}}*,\n\nI wanted to follow up on your recent inquiry about *{{2}}*.\n\n📋 *Your Query:* {{3}}\n\nHave you had a chance to review our proposal?\n\nI'd love to answer any questions you might have. Our offer is valid until *{{4}}*.\n\n_Don't miss out — limited spots available!_`,
    footer: 'Powered by TrueChattr',
    buttonType: 'cta',
    buttons: [
      { type: 'url', text: 'View Proposal', value: 'https://yoursite.com/proposal' },
    ],
  },
  {
    id: 'l3',
    name: 'Special Offer for Lead',
    category: 'promotion',
    headerType: 'image',
    headerText: '',
    body: `🎉 *Exclusive Offer for You, {{1}}!*\n\nAs a valued prospect, we're offering you a special *{{2}}% discount* on *{{3}}*!\n\n💥 *Offer valid till:* {{4}}\n\n✅ {{5}}\n✅ Free consultation included\n✅ No hidden charges\n\n_This is a limited-time offer exclusively for you._`,
    footer: 'T&C apply',
    buttonType: 'cta',
    buttons: [
      { type: 'url',   text: 'Claim Offer Now', value: 'https://yoursite.com/offer' },
      { type: 'phone', text: 'Call Us',          value: '+91 XXXXX XXXXX' },
    ],
  },
  {
    id: 'l4',
    name: 'Demo / Meeting Invite',
    category: 'sales',
    headerType: 'text',
    headerText: '📅 Meeting Invitation',
    body: `Hi *{{1}}*,\n\nThank you for your interest in *{{2}}*! 🙌\n\nI'd like to invite you for a *FREE demo/consultation* where we'll:\n\n🔹 Understand your specific needs\n🔹 Show you exactly how we can help\n🔹 Answer all your questions live\n\n📅 *Suggested Time:* {{3}}\n📍 *Mode:* {{4}}\n\nDoes this work for you?`,
    footer: 'Reply to reschedule anytime',
    buttonType: 'quick_reply',
    buttons: [
      { type: 'quick_reply', text: '✅ Confirm',    value: 'CONFIRM_DEMO' },
      { type: 'quick_reply', text: '🔄 Reschedule', value: 'RESCHEDULE' },
      { type: 'quick_reply', text: '❌ Not interested', value: 'NOT_INTERESTED' },
    ],
  },
  {
    id: 'l5',
    name: 'Re-engagement / Winback',
    category: 'reengagement',
    headerType: 'text',
    headerText: '💔 We Miss You!',
    body: `Hey *{{1}}*! 👋\n\nIt's been a while since we last spoke, and we wanted to check in.\n\nA lot has changed at *{{2}}* — here's what's new:\n\n🆕 {{3}}\n🎁 {{4}}\n⚡ {{5}}\n\nWe'd love to reconnect and see how we can help you achieve your goals.\n\n_Reaching out with a special returning-customer offer just for you!_`,
    footer: 'Reply STOP to opt out',
    buttonType: 'cta',
    buttons: [
      { type: 'url', text: 'See What\'s New', value: 'https://yoursite.com/updates' },
    ],
  },
  {
    id: 'l6',
    name: 'Lead Qualification',
    category: 'nurture',
    headerType: 'none',
    headerText: '',
    body: `Hi *{{1}}*! 👋\n\nThanks for your interest in *{{2}}*.\n\nTo help you better, I have a quick question:\n\n🤔 *{{3}}?*\n\nYour answer helps us tailor the perfect solution for you.\n\nWe work with businesses of all sizes and have helped *500+ clients* achieve their goals.\n\n_Feel free to ask anything — no pressure at all!_`,
    footer: 'Powered by TrueChattr',
    buttonType: 'quick_reply',
    buttons: [
      { type: 'quick_reply', text: 'Option A', value: 'OPTION_A' },
      { type: 'quick_reply', text: 'Option B', value: 'OPTION_B' },
      { type: 'quick_reply', text: 'Tell me more', value: 'MORE_INFO' },
    ],
  },
];

const EMPTY_LEAD_FORM = {
  name: '', phone: '', email: '',
  source: 'whatsapp', status: 'new', notes: '',
};

const EMPTY_MSG_FORM = {
  name: '',
  category: 'sales',
  headerType: 'none',
  headerText: '',
  headerMediaUrl: '',
  body: '',
  footer: '',
  buttonType: 'none',
  buttons: [],
};

// ─────────────────────────────────────────────────────────────────────────────
// WHATSAPP PHONE PREVIEW
// ─────────────────────────────────────────────────────────────────────────────

function WhatsAppPreview({ template, previewVars = {} }) {
  const renderBody = (text) => {
    if (!text) return '';
    let r = text;
    r = r.replace(/\{\{(\d+)\}\}/g, (_, n) => previewVars[n] || `[Var ${n}]`);
    r = r.replace(/\*(.*?)\*/g, '<strong>$1</strong>');
    r = r.replace(/_(.*?)_/g,   '<em>$1</em>');
    r = r.replace(/~(.*?)~/g,   '<del>$1</del>');
    r = r.replace(/\n/g, '<br/>');
    return r;
  };

  const now = new Date();
  const timeStr = `${now.getHours()}:${String(now.getMinutes()).padStart(2, '0')}`;

  return (
    <div className="flex flex-col items-center">
      <div className="relative w-[300px] bg-gray-900 rounded-[38px] p-3 shadow-2xl border-4 border-gray-800">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-20 h-5 bg-gray-900 rounded-b-2xl z-10" />
        <div className="bg-[#ECE5DD] rounded-[28px] overflow-hidden">
          {/* Status bar */}
          <div className="bg-[#075E54] px-4 py-3 flex items-center gap-3 pt-7">
            <div className="w-8 h-8 rounded-full bg-emerald-400 flex items-center justify-center text-white font-bold text-xs">TC</div>
            <div>
              <p className="text-white text-xs font-semibold">TrueChattr</p>
              <p className="text-emerald-200 text-[10px]">Business Account</p>
            </div>
          </div>
          {/* Chat area */}
          <div className="min-h-[380px] p-3 space-y-2 relative"
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none'%3E%3Cg fill='%23b2dfdb' fill-opacity='0.15'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }}
          >
            <div className="flex justify-start">
              <div className="max-w-[88%] bg-white rounded-2xl rounded-tl-sm shadow-sm overflow-hidden">
                {/* Header */}
                {template.headerType === 'image' && (
                  template.headerMediaUrl
                    ? <img src={template.headerMediaUrl} alt="header" className="w-full h-36 object-cover" />
                    : <div className="w-full h-36 bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center gap-2">
                        <Image className="w-8 h-8 text-white/60" />
                        <span className="text-white/70 text-xs">Image Header</span>
                      </div>
                )}
                {template.headerType === 'video' && (
                  <div className="w-full h-36 bg-gray-800 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    </div>
                  </div>
                )}
                {template.headerType === 'document' && (
                  <div className="bg-gray-100 px-3 py-2 flex items-center gap-2 border-b">
                    <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700">Document.pdf</p>
                      <p className="text-[10px] text-gray-400">PDF • Tap to open</p>
                    </div>
                  </div>
                )}
                {template.headerType === 'text' && template.headerText && (
                  <div className="px-3 pt-3 pb-1">
                    <p className="font-bold text-gray-900 text-xs" dangerouslySetInnerHTML={{ __html: renderBody(template.headerText) }} />
                  </div>
                )}
                {/* Body */}
                <div className="px-3 py-2">
                  <p
                    className="text-xs text-gray-800 leading-relaxed"
                    dangerouslySetInnerHTML={{
                      __html: renderBody(template.body) ||
                        '<span class="text-gray-400 italic">Your message appears here…</span>'
                    }}
                  />
                </div>
                {/* Footer */}
                {template.footer && (
                  <div className="px-3 pb-2">
                    <p className="text-[10px] text-gray-400">{template.footer}</p>
                  </div>
                )}
                <div className="px-3 pb-2 flex justify-end">
                  <span className="text-[9px] text-gray-400 flex items-center gap-0.5">
                    {timeStr} <CheckCheck className="w-2.5 h-2.5 text-blue-500" />
                  </span>
                </div>
                {/* CTA Buttons */}
                {template.buttonType === 'cta' && template.buttons?.length > 0 && (
                  <div className="border-t border-gray-100">
                    {template.buttons.map((btn, i) => (
                      <button key={i} className="w-full py-2 text-center text-[#128C7E] text-xs font-semibold hover:bg-gray-50 flex items-center justify-center gap-1.5 border-b border-gray-100 last:border-b-0">
                        {btn.type === 'url'   && <Link2 className="w-3 h-3" />}
                        {btn.type === 'phone' && <Phone className="w-3 h-3" />}
                        {btn.text || 'Button'}
                        <ArrowRight className="w-2.5 h-2.5" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Quick Reply Buttons */}
            {template.buttonType === 'quick_reply' && template.buttons?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pl-1 mt-1">
                {template.buttons.map((btn, i) => (
                  <div key={i} className="bg-white rounded-full px-2.5 py-1 text-[#128C7E] text-[10px] font-semibold shadow-sm border border-gray-100">
                    {btn.text || `Reply ${i + 1}`}
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Input bar */}
          <div className="bg-[#F0F0F0] px-3 py-2 flex items-center gap-2">
            <div className="flex-1 bg-white rounded-full px-3 py-1.5 text-[10px] text-gray-400 flex items-center">
              <Smile className="w-3 h-3 text-gray-400 mr-1.5" />Type a message
            </div>
            <div className="w-7 h-7 rounded-full bg-[#128C7E] flex items-center justify-center">
              <Send className="w-3 h-3 text-white" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FORMAT TOOLBAR
// ─────────────────────────────────────────────────────────────────────────────

function FormatToolbar({ onInsert, detectedVars }) {
  const tools = [
    { label: 'Bold',          icon: Bold,          wrap: ['*', '*']    },
    { label: 'Italic',        icon: Italic,        wrap: ['_', '_']    },
    { label: 'Strikethrough', icon: Strikethrough, wrap: ['~', '~']    },
    { label: 'Variable',      icon: Hash,          wrap: ['{{', '}}']  },
  ];
  return (
    <div className="flex items-center gap-1 p-2 bg-gray-50 border border-gray-200 rounded-xl mb-2">
      <span className="text-xs text-gray-400 mr-1 font-medium">Format:</span>
      {tools.map(t => (
        <button
          key={t.label} type="button" title={t.label}
          onClick={() => onInsert(t.wrap)}
          className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-white hover:border hover:border-gray-200 hover:shadow-sm transition-all"
        >
          <t.icon className="w-3.5 h-3.5" />
        </button>
      ))}
      <div className="ml-auto flex items-center gap-1 text-xs text-gray-400">
        <span className="px-1.5 py-0.5 bg-gray-200 rounded text-[10px]">{'{{1}}'}</span>
        <span>= variable</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BUTTON EDITOR
// ─────────────────────────────────────────────────────────────────────────────

function ButtonEditor({ buttonType, buttons, onChange }) {
  const addButton = () => {
    if (buttonType === 'cta') {
      if (buttons.length >= 2) { toast.error('Max 2 CTA buttons'); return; }
      onChange([...buttons, { type: 'url', text: '', value: '' }]);
    } else {
      if (buttons.length >= 3) { toast.error('Max 3 quick reply buttons'); return; }
      onChange([...buttons, { type: 'quick_reply', text: '', value: '' }]);
    }
  };
  const update = (i, field, val) => {
    const updated = [...buttons]; updated[i] = { ...updated[i], [field]: val }; onChange(updated);
  };
  const remove = (i) => onChange(buttons.filter((_, idx) => idx !== i));

  if (buttonType === 'none') return null;
  return (
    <div className="space-y-3">
      <AnimatePresence>
        {buttons.map((btn, i) => (
          <motion.div
            key={i} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {buttonType === 'cta' ? `CTA Button ${i + 1}` : `Quick Reply ${i + 1}`}
              </span>
              <button onClick={() => remove(i)} className="p-1 rounded-lg hover:bg-red-50 hover:text-red-500 text-gray-400 transition-colors">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Button Text</Label>
                <Input value={btn.text} onChange={e => update(i, 'text', e.target.value)} placeholder={buttonType === 'cta' ? 'Shop Now' : 'Yes, confirm'} maxLength={25} className="h-8 text-xs rounded-lg" />
              </div>
              {buttonType === 'cta' && (
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">Type</Label>
                  <Select value={btn.type} onValueChange={v => update(i, 'type', v)}>
                    <SelectTrigger className="h-8 text-xs rounded-lg"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="url"><Link2 className="w-3 h-3 inline mr-1" />URL</SelectItem>
                      <SelectItem value="phone"><Phone className="w-3 h-3 inline mr-1" />Phone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            {buttonType === 'cta' && (
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">{btn.type === 'url' ? 'URL' : 'Phone Number'}</Label>
                <Input value={btn.value} onChange={e => update(i, 'value', e.target.value)} placeholder={btn.type === 'url' ? 'https://yoursite.com' : '+91 XXXXX XXXXX'} className="h-8 text-xs rounded-lg" />
              </div>
            )}
            {buttonType === 'quick_reply' && (
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">Reply Payload (optional)</Label>
                <Input value={btn.value} onChange={e => update(i, 'value', e.target.value)} placeholder="CONFIRM" className="h-8 text-xs rounded-lg" />
              </div>
            )}
          </motion.div>
        ))}
      </AnimatePresence>
      <button onClick={addButton} type="button"
        className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border-2 border-dashed border-gray-300 text-xs font-medium text-gray-500 hover:border-emerald-400 hover:text-emerald-600 hover:bg-emerald-50/50 transition-all"
      >
        <Plus className="w-3.5 h-3.5" /> Add {buttonType === 'cta' ? 'CTA Button' : 'Quick Reply'}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export default function Leads() {
  // ── State ────────────────────────────────────────────────────────────────
  const [leads,          setLeads]          = useState([]);
  const [selectedLeads,  setSelectedLeads]  = useState([]);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [statusFilter,   setStatusFilter]   = useState('all');
  const [sourceFilter,   setSourceFilter]   = useState('all');
  const [loading,        setLoading]        = useState(true);
  const [dialogOpen,     setDialogOpen]     = useState(false);
  const [msgDialogOpen,  setMsgDialogOpen]  = useState(false);
  const [editingLead,    setEditingLead]    = useState(null);
  const [savingLead,     setSavingLead]     = useState(false);
  const [deletingId,     setDeletingId]     = useState(null);
  const [formData,       setFormData]       = useState(EMPTY_LEAD_FORM);
  const [sending,        setSending]        = useState(false);
  const [starterOpen,    setStarterOpen]    = useState(false);
  const [msgTab,         setMsgTab]         = useState('compose');
  const [msgForm,        setMsgForm]        = useState(EMPTY_MSG_FORM);
  const [previewVars,    setPreviewVars]    = useState({});
  const [imgPreview,     setImgPreview]     = useState(false);
  const bodyRef = useRef(null);

  //useEffect(() => { fetchLeads(); }, [statusFilter, sourceFilter]);


  const fetchLeads = useCallback(async () => {
  try {
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.append('status', statusFilter);
    if (sourceFilter !== 'all') params.append('source', sourceFilter);
    const { data } = await api.get(`/leads?${params.toString()}`);
    setLeads(data.leads || []);
  } catch {
    toast.error('Failed to load leads');
    setLeads([]);
  } finally {
    setLoading(false);
  }
}, [statusFilter, sourceFilter]);  // ← dependencies yahan

useEffect(() => {
  fetchLeads();
}, [fetchLeads]);  // ← ab sirf fetchLeads chahiye
  

  // ── Computed ──────────────────────────────────────────────────────────────
  const filtered = Array.isArray(leads) ? leads.filter(l =>
    (l.name?.toLowerCase()  || '').includes(searchQuery.toLowerCase()) ||
    (l.phone               || '').includes(searchQuery) ||
    (l.email?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  ) : [];

  const converted = leads.filter(l => l.status === 'converted').length;
  const qualified  = leads.filter(l => l.status === 'qualified').length;
  const convRate   = leads.length > 0 ? ((converted / leads.length) * 100).toFixed(0) : 0;

  const detectedVars = useMemo(() => {
  return [...new Set(
    [...(msgForm.body?.matchAll(/\{\{(\d+)\}\}/g) || [])]
      .map(m => m[1])
      .sort((a, b) => Number(a) - Number(b))
  )];
}, [msgForm.body]);

// ── Format toolbar insert ─────────────────────────────────────────────────
const handleInsert = useCallback((wrap) => {
  const el = bodyRef.current;
  if (!el) return;
  const start = el.selectionStart;
  const end   = el.selectionEnd;
  const sel   = msgForm.body.substring(start, end);
  let insertion;
  if (wrap[0] === '{{') {
    const next = (detectedVars.length > 0 ? Math.max(...detectedVars.map(Number)) + 1 : 1).toString();
    insertion = `{{${next}}}`;
  } else {
    insertion = `${wrap[0]}${sel || 'text'}${wrap[1]}`;
  }
  const newBody = msgForm.body.substring(0, start) + insertion + msgForm.body.substring(end);
  setMsgForm(f => ({ ...f, body: newBody }));
  setTimeout(() => {
    el.focus();
    el.setSelectionRange(start + insertion.length, start + insertion.length);
  }, 0);
}, [msgForm.body, detectedVars]);

  // ── Save lead ─────────────────────────────────────────────────────────────
  const handleSaveLead = async () => {
    if (!formData.phone || formData.phone.trim().length < 10) {
      toast.error('Valid phone number is required'); return;
    }
    setSavingLead(true);
    try {
      if (editingLead) {
        await api.put(`/leads/${editingLead.id}`, formData);
        toast.success('Lead updated successfully');
      } else {
        await api.post('/leads', formData);
        toast.success('Lead created successfully');
      }
      setDialogOpen(false);
      setEditingLead(null);
      setFormData(EMPTY_LEAD_FORM);
      fetchLeads();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save lead');
    } finally {
      setSavingLead(false);
    }
  };

  // ── Delete lead ───────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    setDeletingId(id);
    try {
      await api.delete(`/leads/${id}`);
      toast.success('Lead deleted');
      setSelectedLeads(s => s.filter(sid => sid !== id));
      fetchLeads();
    } catch {
      toast.error('Failed to delete lead');
    } finally {
      setDeletingId(null);
    }
  };

  const openEdit = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name || '', phone: lead.phone || '', email: lead.email || '',
      source: lead.source || 'whatsapp', status: lead.status || 'new', notes: lead.notes || '',
    });
    setDialogOpen(true);
  };
  const openAdd = () => { setEditingLead(null); setFormData(EMPTY_LEAD_FORM); setDialogOpen(true); };

  // ── Selection ─────────────────────────────────────────────────────────────
  const handleSelectAll = (checked) => setSelectedLeads(checked ? filtered.map(l => l.id) : []);
  const handleSelectOne = (id, checked) => setSelectedLeads(s => checked ? [...s, id] : s.filter(sid => sid !== id));

  // ── Send WhatsApp ─────────────────────────────────────────────────────────
const handleSendWhatsApp = async () => {
  if (!msgForm.body?.trim() || msgForm.body.trim().length < 5) {
    toast.error('Please enter a message'); return;
  }
  const targets = selectedLeads.length > 0
    ? filtered.filter(l => selectedLeads.includes(l.id))
    : filtered;
  const withPhone = targets.filter(l => l.phone);
  if (!withPhone.length) { toast.error('No leads have phone numbers'); return; }
  if (!confirm(`Send message to ${withPhone.length} lead(s)?`)) return;
  setSending(true);
  try {

    // ✅ FIX 1: Variables replace karo body mein
    let processedBody = msgForm.body.trim();
    Object.entries(previewVars).forEach(([key, value]) => {
      processedBody = processedBody.replace(
        new RegExp(`\\{\\{${key}\\}\\}`, 'g'), 
        value
      );
    });

    // ✅ FIX 2: Buttons ko body ke neeche append karo
    if (msgForm.buttonType !== 'none' && msgForm.buttons?.length > 0) {
      processedBody += '\n';
      msgForm.buttons.forEach(btn => {
        if (btn.type === 'url' && btn.value) {
          processedBody += `\n🔗 ${btn.text}: ${btn.value}`;
        } else if (btn.type === 'phone' && btn.value) {
          processedBody += `\n📞 ${btn.text}: ${btn.value}`;
        } else if (btn.type === 'quick_reply' && btn.text) {
          processedBody += `\n↩️ ${btn.text}`;
        }
      });
    }

    // ✅ FIX 3: message mein processedBody use karo (pehle sirf msgForm.body.trim() tha)
    const { data } = await api.post('/leads/send-whatsapp', {
      message:    processedBody,
      header:     msgForm.headerType !== 'none' ? { type: msgForm.headerType, text: msgForm.headerText, mediaUrl: msgForm.headerMediaUrl } : null,
      footer:     msgForm.footer || '',
      buttonType: msgForm.buttonType,
      buttons:    msgForm.buttonType !== 'none' ? msgForm.buttons : [],
      variables:  previewVars,
      leadIds:    selectedLeads.length > 0 ? selectedLeads : null,
    });

    toast.success(data.message || `Message sent to ${data.sent} lead(s)`);
    setMsgForm(EMPTY_MSG_FORM);
    setPreviewVars({});
    setSelectedLeads([]);
    setMsgDialogOpen(false);
  } catch (err) {
    toast.error(err.response?.data?.error || 'Failed to send messages');
  } finally {
    setSending(false);
  }
};

  // ── Load starter template ─────────────────────────────────────────────────
  const loadStarter = (starter) => {
    setMsgForm({
      name:          starter.name,
      category:      starter.category,
      headerType:    starter.headerType || 'none',
      headerText:    starter.headerText || '',
      headerMediaUrl:'',
      body:          starter.body,
      footer:        starter.footer || '',
      buttonType:    starter.buttonType || 'none',
      buttons:       starter.buttons ? [...starter.buttons] : [],
    });
    setPreviewVars({});
    setStarterOpen(false);
    setMsgTab('compose');
    setTimeout(() => setMsgDialogOpen(true), 80);
  };

  // ── Export CSV ─────────────────────────────────────────────────────────────
  const exportLeads = () => {
    if (!filtered.length) { toast.error('No leads to export'); return; }
    const csv = [
      ['Name', 'Phone', 'Email', 'Source', 'Status', 'Notes', 'Created At'],
      ...filtered.map(l => [
        l.name || '', l.phone || '', l.email || '', l.source || '',
        l.status || '', (l.notes || '').replace(/,/g, ';'),
        format(new Date(l.created_at), 'yyyy-MM-dd HH:mm'),
      ]),
    ].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `leads-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('Leads exported!');
  };

  // ── Open message dialog ───────────────────────────────────────────────────
  const openMsgDialog = () => {
    setMsgForm(EMPTY_MSG_FORM);
    setPreviewVars({});
    setMsgTab('compose');
    setMsgDialogOpen(true);
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading && !leads.length) {
    return (
      <div className="h-[calc(100vh-140px)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-14 h-14">
            <div className="absolute inset-0 rounded-full border-4 border-emerald-100" />
            <div className="absolute inset-0 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
          </div>
          <p className="text-sm text-gray-400 font-medium">Loading leads…</p>
        </div>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8" data-testid="leads-page">

      {/* ── Header ── */}
      <motion.div {...fadeUp(0)} className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight flex items-center gap-2">
            <Users className="w-7 h-7 text-emerald-600" />
            Leads
          </h1>
          <p className="mt-1 text-sm text-gray-400">
            Manage & message leads from WhatsApp
            {selectedLeads.length > 0 && (
              <span className="ml-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold">
                {selectedLeads.length} selected
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={fetchLeads} disabled={loading}
            className="p-2 rounded-xl border border-gray-200 bg-white text-gray-500 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={exportLeads} disabled={!filtered.length}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50 transition-all disabled:opacity-50"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
          <button onClick={openMsgDialog} disabled={!filtered.length}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-sm font-semibold text-emerald-700 hover:bg-emerald-100 transition-all disabled:opacity-50"
          >
            <Send className="w-4 h-4" /> Send WhatsApp
          </button>
          <button onClick={openAdd}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 transition-all"
          >
            <Plus className="w-4 h-4" /> Add Lead
          </button>
        </div>
      </motion.div>

      {/* ── Stats ── */}
      <motion.div {...fadeUp(0.06)} className="flex flex-wrap gap-3">
        {[
          { icon: Users,      label: 'Total Leads',  value: leads.length },
          { icon: UserCheck,  label: 'Qualified',    value: qualified    },
          { icon: TrendingUp, label: 'Converted',    value: converted    },
          { icon: BarChart2,  label: 'Conv. Rate',   value: `${convRate}%` },
        ].map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-center gap-2.5 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm">
            <div className="w-7 h-7 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Icon className="w-3.5 h-3.5 text-emerald-600" />
            </div>
            <div>
              <span className="text-xs text-gray-400 block leading-none mb-0.5">{label}</span>
              <span className="text-sm font-bold text-gray-900 dark:text-white">{value}</span>
            </div>
          </div>
        ))}
      </motion.div>

      {/* ── Filters ── */}
      <motion.div {...fadeUp(0.1)} className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <Input
            placeholder="Search by name, phone, email…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 h-10 rounded-xl border-gray-200 focus:border-emerald-400 text-sm"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 h-10 rounded-xl border-gray-200 text-sm focus:border-emerald-400">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            {Object.entries(STATUS_MAP).map(([v, { label }]) => (
              <SelectItem key={v} value={v}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-36 h-10 rounded-xl border-gray-200 text-sm focus:border-emerald-400">
            <SelectValue placeholder="All Sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sources</SelectItem>
            {Object.entries(SOURCE_ICON).map(([v, emoji]) => (
              <SelectItem key={v} value={v}>{emoji} {v.charAt(0).toUpperCase() + v.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {searchQuery && (
          <span className="text-xs text-gray-400">{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
        )}
      </motion.div>

      {/* ── Table ── */}
      <motion.div {...fadeUp(0.14)}>
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-100 bg-gray-50/70 dark:bg-gray-800/70">
                <TableHead className="w-10 pl-4">
                  <Checkbox
                    checked={selectedLeads.length === filtered.length && filtered.length > 0}
                    onCheckedChange={handleSelectAll}
                    className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                  />
                </TableHead>
                <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</TableHead>
                <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Contact</TableHead>
                <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Source</TableHead>
                <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</TableHead>
                <TableHead className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Created</TableHead>
                <TableHead className="text-right pr-4 text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <AnimatePresence>
                {filtered.length > 0 ? (
                  filtered.map((lead, i) => {
                    const st = STATUS_MAP[lead.status] || STATUS_MAP.new;
                    const isSel = selectedLeads.includes(lead.id);
                    return (
                      <motion.tr
                        key={lead.id}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ delay: i * 0.02 }}
                        className={`border-b border-gray-50 dark:border-gray-700 transition-colors ${isSel ? 'bg-emerald-50/50' : 'hover:bg-gray-50/60 dark:hover:bg-gray-700/30'}`}
                      >
                        <TableCell className="pl-4">
                          <Checkbox
                            checked={isSel}
                            onCheckedChange={checked => handleSelectOne(lead.id, checked)}
                            className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-emerald-600" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-gray-900 dark:text-white">{lead.name || 'Unknown'}</p>
                              {lead.notes && <p className="text-xs text-gray-400 truncate max-w-[160px]">{lead.notes}</p>}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                              <Phone className="w-3 h-3 text-gray-400" />
                              <span className="font-mono">{lead.phone}</span>
                            </div>
                            {lead.email && (
                              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                                <Mail className="w-3 h-3" /><span>{lead.email}</span>
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs font-medium text-gray-600 capitalize">
                            <span>{SOURCE_ICON[lead.source] || '📌'}</span>
                            {lead.source || 'unknown'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${st.pill}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                            {st.label}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs text-gray-400">
                          {format(new Date(lead.created_at), 'MMM dd, yyyy')}
                        </TableCell>
                        <TableCell className="text-right pr-4">
                          <div className="flex justify-end gap-1">
                            <button
                              onClick={() => {
                                setSelectedLeads([lead.id]);
                                openMsgDialog();
                              }}
                              className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                              title="Send WhatsApp"
                            >
                              <Send className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => openEdit(lead)} className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all">
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={() => handleDelete(lead.id)} disabled={deletingId === lead.id} className="p-1.5 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 transition-all disabled:opacity-50">
                              {deletingId === lead.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                            </button>
                          </div>
                        </TableCell>
                      </motion.tr>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <div className="text-center py-16">
                        <div className="w-14 h-14 mx-auto rounded-2xl bg-gray-50 flex items-center justify-center mb-4">
                          <Users className="w-7 h-7 text-gray-300" />
                        </div>
                        <p className="text-gray-900 dark:text-white font-semibold mb-1">
                          {searchQuery || statusFilter !== 'all' || sourceFilter !== 'all' ? 'No leads found' : 'No leads yet'}
                        </p>
                        <p className="text-sm text-gray-400 mb-5">
                          {searchQuery ? `No results for "${searchQuery}"` : 'Start capturing leads from WhatsApp conversations.'}
                        </p>
                        {!searchQuery && statusFilter === 'all' && sourceFilter === 'all' && (
                          <button onClick={openAdd} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors">
                            <Plus className="w-4 h-4" /> Add First Lead
                          </button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </AnimatePresence>
            </TableBody>
          </Table>
        </div>
      </motion.div>

      {/* ═══════════════════════════════════════════════════════════════════════
          ADD / EDIT LEAD DIALOG
      ═══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl rounded-2xl border-gray-100 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              {editingLead ? 'Edit Lead' : 'Add New Lead'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-5 mt-2">
            <div className="grid grid-cols-2 gap-4">
              {[
                { id: 'name',  label: 'Name',    ph: 'John Doe',         type: 'text'  },
                { id: 'phone', label: 'Phone *', ph: '+91 98765 43210',  type: 'tel'   },
                { id: 'email', label: 'Email',   ph: 'john@example.com', type: 'email' },
              ].map(({ id, label, ph, type }) => (
                <div key={id} className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</Label>
                  <Input
                    type={type} value={formData[id]}
                    onChange={e => setFormData({ ...formData, [id]: e.target.value })}
                    placeholder={ph}
                    className="h-10 rounded-xl border-gray-200 focus:border-emerald-400 text-sm"
                  />
                </div>
              ))}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Source</Label>
                <Select value={formData.source} onValueChange={v => setFormData({ ...formData, source: v })}>
                  <SelectTrigger className="h-10 rounded-xl border-gray-200 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SOURCE_ICON).map(([v, emoji]) => (
                      <SelectItem key={v} value={v}>{emoji} {v.charAt(0).toUpperCase() + v.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</Label>
                <Select value={formData.status} onValueChange={v => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="h-10 rounded-xl border-gray-200 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_MAP).map(([v, { label }]) => (
                      <SelectItem key={v} value={v}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this lead…"
                rows={3}
                className="rounded-xl border-gray-200 focus:border-emerald-400 text-sm resize-none"
              />
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setDialogOpen(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all">
                Cancel
              </button>
              <button
                onClick={handleSaveLead} disabled={savingLead}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 disabled:opacity-60 transition-all"
              >
                {savingLead ? <><Loader2 className="w-4 h-4 animate-spin" />Saving…</> : editingLead ? 'Update Lead' : 'Add Lead'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════════════
          STARTER TEMPLATES MODAL
      ═══════════════════════════════════════════════════════════════════════ */}
      <AnimatePresence>
        {starterOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setStarterOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[82vh] overflow-hidden border border-gray-200"
            >
              <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-emerald-600" /> Lead Message Templates
                  </h2>
                  <p className="text-sm text-gray-400 mt-0.5">Pre-built templates designed for lead outreach & nurturing</p>
                </div>
                <button onClick={() => setStarterOpen(false)} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(82vh-100px)] grid grid-cols-1 sm:grid-cols-2 gap-4">
                {STARTER_TEMPLATES.map(starter => {
                  const cat = TEMPLATE_CATEGORIES.find(c => c.value === starter.category);
                  return (
                    <button
                      key={starter.id}
                      onClick={() => loadStarter(starter)}
                      className="text-left p-5 rounded-xl border-2 border-gray-100 hover:border-emerald-300 hover:shadow-md hover:bg-emerald-50/30 transition-all duration-200 group"
                    >
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cat?.color}`}>
                          {cat?.emoji} {cat?.label}
                        </span>
                        {starter.headerType !== 'none' && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">{starter.headerType}</span>
                        )}
                      </div>
                      <h3 className="font-bold text-gray-900 dark:text-white text-sm mb-1 group-hover:text-emerald-700">{starter.name}</h3>
                      <p className="text-xs text-gray-400 line-clamp-2 leading-relaxed">
                        {starter.body.replace(/\*(.*?)\*/g, '$1').replace(/\n/g, ' ').slice(0, 80)}...
                      </p>
                      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                        {starter.buttonType !== 'none' && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded-full">
                            {starter.buttons.length} button{starter.buttons.length > 1 ? 's' : ''}
                          </span>
                        )}
                        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded-full">
                          {(starter.body.match(/\{\{\d+\}\}/g) || []).length} variables
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══════════════════════════════════════════════════════════════════════
          SEND WHATSAPP DIALOG — Full Template Builder + Live Preview
      ═══════════════════════════════════════════════════════════════════════ */}
      <Dialog open={msgDialogOpen} onOpenChange={open => !open && setMsgDialogOpen(false)}>
        <DialogContent className="sm:max-w-5xl max-h-[95vh] overflow-hidden p-0 rounded-2xl border-gray-100 shadow-2xl">
          <div className="flex flex-col h-full max-h-[95vh]">

            {/* Dialog Header */}
            <DialogHeader className="px-6 pt-6 pb-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Send className="w-5 h-5 text-emerald-600" /> Send WhatsApp Message
                  </DialogTitle>
                  <DialogDescription className="text-sm text-gray-400 mt-0.5">
                    {selectedLeads.length > 0
                      ? `Sending to ${selectedLeads.length} selected lead(s)`
                      : `Sending to all ${filtered.length} lead(s) matching current filters`}
                  </DialogDescription>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); setMsgDialogOpen(false); setTimeout(() => setStarterOpen(true), 80); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 transition-all"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Use Template
                  </button>
                </div>
              </div>

              {/* Recipient info */}
              <div className="flex items-center gap-3 p-3 mt-3 rounded-xl bg-emerald-50 border border-emerald-100">
                <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-3.5 h-3.5 text-emerald-600" />
                </div>
                <p className="text-sm text-gray-700">
                  <strong className="text-emerald-700">
                    {selectedLeads.length > 0 ? selectedLeads.length : filtered.length}
                  </strong> lead(s) will receive this message.
                  {filtered.filter(l => !l.phone).length > 0 && (
                    <span className="text-gray-400 ml-1 text-xs">
                      ({filtered.filter(l => !l.phone).length} without phone skipped)
                    </span>
                  )}
                </p>
              </div>
            </DialogHeader>

            {/* Body */}
            <div className="flex flex-1 overflow-hidden min-h-0">

              {/* ── Left: Editor ── */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5 border-r border-gray-100">

                {/* ── HEADER ── */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Header (Optional)</Label>
                  <div className="flex flex-wrap gap-2">
                    {HEADER_TYPES.map(ht => (
                      <button
                        key={ht.value} type="button"
                        onClick={() => setMsgForm(f => ({ ...f, headerType: ht.value }))}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-all ${msgForm.headerType === ht.value ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      >
                        <ht.icon className="w-3 h-3" /> {ht.label}
                      </button>
                    ))}
                  </div>
                  {msgForm.headerType === 'text' && (
                    <Input
                      value={msgForm.headerText}
                      onChange={e => setMsgForm(f => ({ ...f, headerText: e.target.value }))}
                      placeholder="✅ Important Update"
                      maxLength={60}
                      className="h-10 rounded-xl text-sm"
                    />
                  )}
                  {['image', 'video', 'document'].includes(msgForm.headerType) && (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={msgForm.headerMediaUrl}
                          onChange={e => { setMsgForm(f => ({ ...f, headerMediaUrl: e.target.value })); setImgPreview(false); }}
                          placeholder={msgForm.headerType === 'image' ? 'https://cdn.yoursite.com/banner.jpg' : 'Media URL'}
                          className="flex-1 h-10 rounded-xl text-sm"
                        />
                        {msgForm.headerType === 'image' && msgForm.headerMediaUrl && (
                          <button type="button" onClick={() => setImgPreview(v => !v)} className="px-3 rounded-xl border border-gray-200 text-gray-500 hover:border-emerald-300 hover:text-emerald-600 transition-colors">
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <AnimatePresence>
                        {imgPreview && msgForm.headerMediaUrl && (
                          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden rounded-xl border border-gray-200">
                            <img src={msgForm.headerMediaUrl} alt="preview" className="w-full h-32 object-cover" onError={() => { setImgPreview(false); toast.error('Could not load image'); }} />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>

                {/* ── BODY ── */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Message Body <span className="text-rose-400">*</span>
                    <span className="ml-2 text-xs font-normal text-gray-400">{msgForm.body?.length || 0}/1024</span>
                  </Label>
                  <FormatToolbar onInsert={handleInsert} detectedVars={detectedVars} />
                  <Textarea
                    ref={bodyRef}
                    value={msgForm.body}
                    onChange={e => setMsgForm(f => ({ ...f, body: e.target.value }))}
                    placeholder={`Hi *{{1}}*,\n\nThank you for your interest in *{{2}}*!\n\n_Reply to learn more._`}
                    rows={7}
                    maxLength={1024}
                    className="rounded-xl border-gray-200 focus:border-emerald-400 text-sm resize-none font-mono leading-relaxed"
                  />
                  {/* Variable fill inputs */}
                  {detectedVars.length > 0 && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <p className="text-xs font-semibold text-amber-800 mb-2 flex items-center gap-1">
                        <Hash className="w-3.5 h-3.5" /> Fill variable values for preview:
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        {detectedVars.map(v => (
                          <div key={v} className="flex items-center gap-2">
                            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-mono">{`{{${v}}}`}</span>
                            <Input
                              value={previewVars[v] || ''}
                              onChange={e => setPreviewVars(p => ({ ...p, [v]: e.target.value }))}
                              placeholder={`Value ${v}`}
                              className="h-7 text-xs rounded-lg flex-1"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* ── FOOTER ── */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Footer <span className="text-xs font-normal text-gray-400">(optional)</span>
                  </Label>
                  <Input
                    value={msgForm.footer}
                    onChange={e => setMsgForm(f => ({ ...f, footer: e.target.value }))}
                    placeholder="Reply STOP to unsubscribe"
                    maxLength={60}
                    className="h-10 rounded-xl text-sm"
                  />
                </div>

                {/* ── BUTTONS ── */}
                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">Buttons (Optional)</Label>
                  <div className="flex gap-2">
                    {BUTTON_TYPES.map(bt => (
                      <button
                        key={bt.value} type="button"
                        onClick={() => setMsgForm(f => ({ ...f, buttonType: bt.value, buttons: [] }))}
                        className={`px-3 py-1.5 rounded-xl border text-xs font-medium transition-all ${msgForm.buttonType === bt.value ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                      >
                        {bt.label}
                      </button>
                    ))}
                  </div>
                  <ButtonEditor
                    buttonType={msgForm.buttonType}
                    buttons={msgForm.buttons}
                    onChange={buttons => setMsgForm(f => ({ ...f, buttons }))}
                  />
                </div>

                {/* ── WhatsApp Guidelines ── */}
                <div className="flex items-start gap-2.5 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 rounded-xl">
                  <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Use <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">{'{{1}}'}</code>, <code className="bg-blue-100 px-1 rounded">{'{{2}}'}</code> for personalisation. Variables are filled with preview values above. Buttons appear as text links in unsupported clients.
                  </p>
                </div>

                {/* ── Action Buttons ── */}
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => setMsgDialogOpen(false)} disabled={sending}
                    className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSendWhatsApp}
                    disabled={sending || !msgForm.body?.trim()}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold shadow-lg shadow-emerald-200/60 disabled:opacity-60 transition-all"
                  >
                    {sending
                      ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                      : <><Send className="w-4 h-4" /> Send to {selectedLeads.length > 0 ? selectedLeads.length : filtered.length} Lead{(selectedLeads.length || filtered.length) !== 1 ? 's' : ''}</>
                    }
                  </button>
                </div>
              </div>

              {/* ── Right: Live Preview ── */}
              <div className="hidden lg:flex w-[360px] flex-shrink-0 bg-gray-50 dark:bg-gray-800/50 flex-col items-center justify-start p-6 overflow-y-auto gap-4">
                <div className="w-full flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Live Preview</p>
                  <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">Real-time</span>
                </div>
                <WhatsAppPreview template={msgForm} previewVars={previewVars} />
                {/* Stats */}
                <div className="w-full p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
                  <p className="text-xs font-semibold text-gray-500 mb-2">Message Info</p>
                  <div className="space-y-1.5">
                    {[
                      { label: 'Body length',  value: `${msgForm.body?.length || 0}/1024`, warn: (msgForm.body?.length || 0) > 900 },
                      { label: 'Variables',    value: detectedVars.length },
                      { label: 'Buttons',      value: msgForm.buttons?.length || 0 },
                      { label: 'Header',       value: msgForm.headerType === 'none' ? '—' : msgForm.headerType },
                      { label: 'Recipients',   value: selectedLeads.length > 0 ? `${selectedLeads.length} selected` : `${filtered.length} leads` },
                    ].map(({ label, value, warn }) => (
                      <div key={label} className="flex justify-between text-xs">
                        <span className="text-gray-400">{label}</span>
                        <span className={`font-medium ${warn ? 'text-red-500' : 'text-gray-700 dark:text-gray-300'}`}>{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
